import React, { useState, useEffect, useRef, useCallback } from 'react';

interface WhyChurchPlantingStepProps {
  data?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const WhyChurchPlantingStep: React.FC<WhyChurchPlantingStepProps> = ({
  data,
  onComplete,
  onNext,
  onPrevious,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    whyPlanting: '',
    visionForChurch: '',
    howDecisionMade: '',
    additionalThoughts: '',
  });

  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const savedIndicatorTimer = useRef<NodeJS.Timeout | null>(null);
  const latestFormData = useRef(formData);
  const hasPendingChanges = useRef(false);
  const initialDataLoaded = useRef(false);

  // Keep ref in sync so beforeunload/cleanup can access latest data
  useEffect(() => {
    latestFormData.current = formData;
  }, [formData]);

  // Populate form from existing data (saved or draft)
  useEffect(() => {
    if (data) {
      setFormData({
        whyPlanting: data.whyPlanting || '',
        visionForChurch: data.visionForChurch || '',
        howDecisionMade: data.howDecisionMade || '',
        additionalThoughts: data.additionalThoughts || '',
      });
      if (data.submittedAt || data.completed) {
        setHasBeenSubmitted(true);
      }
      initialDataLoaded.current = true;
    }
  }, [data]);

  // Flush save immediately (for beforeunload and cleanup)
  const flushSave = useCallback(async (dataToSave: typeof formData) => {
    // Only save if there's meaningful content
    const hasContent = dataToSave.whyPlanting.trim() ||
      dataToSave.visionForChurch.trim() ||
      dataToSave.howDecisionMade.trim() ||
      dataToSave.additionalThoughts.trim();

    if (!hasContent) return;

    try {
      const { portalCandidateService } = await import('../../api/portalCandidateService');
      await portalCandidateService.submitWhyChurchPlanting({ ...dataToSave, draft: true });
      hasPendingChanges.current = false;
    } catch (err) {
      console.error('Error auto-saving Why Church Planting:', err);
    }
  }, []);

  // Debounced auto-save – fires 1.5s after the last keystroke
  const autoSaveDraft = useCallback(async (dataToSave: typeof formData) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    hasPendingChanges.current = true;

    autoSaveTimer.current = setTimeout(async () => {
      const hasContent = dataToSave.whyPlanting.trim() ||
        dataToSave.visionForChurch.trim() ||
        dataToSave.howDecisionMade.trim() ||
        dataToSave.additionalThoughts.trim();

      if (!hasContent) return;

      setSaveStatus('saving');

      try {
        const { portalCandidateService } = await import('../../api/portalCandidateService');
        await portalCandidateService.submitWhyChurchPlanting({ ...dataToSave, draft: true });
        hasPendingChanges.current = false;
        setSaveStatus('saved');

        // Clear the "saved" indicator after a few seconds
        if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
        savedIndicatorTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
      } catch (err) {
        console.error('Error auto-saving Why Church Planting:', err);
        setSaveStatus('error');
      }
    }, 1500);
  }, []);

  // Save on window/tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasPendingChanges.current) {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
        // fetch with keepalive survives page unload and supports auth headers
        fetch(`${apiBase}/portal-candidates/onboarding/why-church-planting`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...latestFormData.current, draft: true }),
          keepalive: true,
        }).catch(() => { /* best-effort */ });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Cleanup timers and flush on unmount (e.g. navigating to another step)
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
      if (hasPendingChanges.current) {
        flushSave(latestFormData.current);
      }
    };
  }, [flushSave]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      autoSaveDraft(updated);
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.whyPlanting.trim() || !formData.visionForChurch.trim() || !formData.howDecisionMade.trim()) {
      setError('Please answer all required questions before submitting.');
      return;
    }

    // Cancel any pending auto-save since we're doing a real save
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    hasPendingChanges.current = false;

    setIsSubmitting(true);

    try {
      await onComplete(formData);
      setHasBeenSubmitted(true);
      setSaveStatus('saved');
      if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
      savedIndicatorTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save your responses. Please try again.');
      console.error('Error submitting Why Church Planting:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save status indicator
  const renderSaveIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="inline-flex items-center text-xs text-gray-400">
          <svg className="animate-spin mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Saving...
        </span>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <span className="inline-flex items-center text-xs text-green-500">
          <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          All changes saved
        </span>
      );
    }
    if (saveStatus === 'error') {
      return (
        <span className="inline-flex items-center text-xs text-red-400">
          Auto-save failed
        </span>
      );
    }
    return null;
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Church Planting?</h2>
          <p className="text-gray-600">
            We'd love to hear about your calling to church planting. Please take some time to thoughtfully
            answer the following questions.
          </p>
        </div>
        <div className="flex-shrink-0 pt-1">{renderSaveIndicator()}</div>
      </div>

      {hasBeenSubmitted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800 font-medium">
                Your responses have been saved.
                {data?.submittedAt && (
                  <span className="font-normal text-green-600 ml-1">
                    First submitted {new Date(data.submittedAt).toLocaleDateString()}.
                  </span>
                )}
              </p>
              <p className="text-xs text-green-600 mt-0.5">You can update your answers below at any time.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Question 1 */}
          <div>
            <label htmlFor="whyPlanting" className="block text-sm font-semibold text-gray-900 mb-2">
              Why do you want to plant a church? <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Share what has stirred in your heart about church planting. What burden has God placed on you?
            </p>
            <textarea
              id="whyPlanting"
              rows={5}
              value={formData.whyPlanting}
              onChange={(e) => handleChange('whyPlanting', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Tell us about your calling to church planting..."
              required
            />
          </div>

          {/* Question 2 */}
          <div>
            <label htmlFor="visionForChurch" className="block text-sm font-semibold text-gray-900 mb-2">
              What is your vision for the church you want to plant? <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Describe the kind of church you envision. Who will it reach? What will it look like? What community will it serve?
            </p>
            <textarea
              id="visionForChurch"
              rows={5}
              value={formData.visionForChurch}
              onChange={(e) => handleChange('visionForChurch', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Describe your vision for the church..."
              required
            />
          </div>

          {/* Question 3 */}
          <div>
            <label htmlFor="howDecisionMade" className="block text-sm font-semibold text-gray-900 mb-2">
              How did you come to this decision? <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Walk us through the journey that led you to pursue church planting. What key moments, conversations,
              or experiences confirmed this calling?
            </p>
            <textarea
              id="howDecisionMade"
              rows={5}
              value={formData.howDecisionMade}
              onChange={(e) => handleChange('howDecisionMade', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Share the journey that brought you here..."
              required
            />
          </div>

          {/* Question 4 (Optional) */}
          <div>
            <label htmlFor="additionalThoughts" className="block text-sm font-semibold text-gray-900 mb-2">
              Anything else you'd like to share?
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Is there anything else about your church planting vision or calling that you'd like us to know?
            </p>
            <textarea
              id="additionalThoughts"
              rows={4}
              value={formData.additionalThoughts}
              onChange={(e) => handleChange('additionalThoughts', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="(Optional) Share any additional thoughts..."
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : hasBeenSubmitted ? (
              'Update & Continue'
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WhyChurchPlantingStep;
